import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const PRESETS = [15, 30, 45, 60, 90] as const;

interface TimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (minutes: number) => void;
  isActive?: boolean;
  remainingSeconds?: number;
  onCancel?: () => void;
}

export const TimerModal: React.FC<TimerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  isActive,
  remainingSeconds = 0,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-[70] backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed left-0 right-0 z-[70] px-4"
            style={{
              // Above bottom tab bar (+ iOS home indicator), same clearance as MiniPlayer/ShareToast
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4.5rem)',
            }}
          >
            <div className="glass rounded-2xl p-6 max-w-sm mx-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{t('timerModal.title')}</h3>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {isActive && remainingSeconds > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-text-secondary">
                    {t('timerModal.remaining')}: {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, '0')}
                  </p>
                  <button
                    onClick={() => { onCancel?.(); onClose(); }}
                    className="w-full py-3 rounded-xl bg-accent-pink/20 text-accent-pink font-medium hover:bg-accent-pink/30 transition-colors"
                  >
                    {t('timerModal.cancel')}
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((mins) => (
                    <button
                      key={mins}
                      onClick={() => { onSelect(mins); onClose(); }}
                      className="px-4 py-2 rounded-xl glass hover:bg-white/10 transition-colors"
                    >
                      {mins} {t('common.min')}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
